import NewResponsiveImage from "@components/CustomTypography/NewResponsiveImage/NewResponsiveImage"
import {AssetType} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import clsx from "clsx"
import {AnimatePresence, motion} from "framer-motion"
import {useRef} from "react"

import styles from "./HeroSlideContent.module.scss"

type Props = {
	desktopAsset?: AssetType
	mobileAsset?: AssetType
	alt: string
	currentIndex?: number
}

const IMAGE_FADE = {duration: 0.2, ease: "easeInOut" as const}
const WRAPPER_FADE = {duration: 0.8, ease: "easeInOut" as const}
const GRAPHIC_LAYOUT = {
	type: "spring" as const,
	bounce: 0.3,
	duration: 0.55,
}

type LayoutFloatProps = {
	className: string
	src: string
}

const LayoutFloat = ({className, src}: LayoutFloatProps) => (
	<motion.img
		alt={""}
		className={className}
		layout
		layoutDependency={className}
		src={src}
		transition={{layout: GRAPHIC_LAYOUT}}
	/>
)

type SlideImagesProps = {
	alt: string
	desktopAsset?: AssetType
	desktopSrc?: string
	mobileAsset?: AssetType
	mobileSrc?: string
	showDesktop: boolean
	showMobile: boolean
	swapOnSmallScreens: boolean
}

const SlideImages = ({
	alt,
	desktopAsset,
	desktopSrc,
	mobileAsset,
	mobileSrc,
	showDesktop,
	showMobile,
	swapOnSmallScreens,
}: SlideImagesProps) => (
	<>
		{showDesktop && desktopSrc && (
			<NewResponsiveImage
				alt={desktopAsset?.alt || alt}
				containerClassName={clsx(
					styles.mainImgContainer,
					swapOnSmallScreens && styles.desktopOnly,
				)}
				height={desktopAsset?.height || 1024}
				imageClassName={styles.mainImg}
				src={desktopSrc}
				width={desktopAsset?.width || 1920}
			/>
		)}
		{showMobile && mobileSrc && (
			<NewResponsiveImage
				alt={mobileAsset?.alt || alt}
				containerClassName={clsx(
					styles.mainImgContainer,
					swapOnSmallScreens && styles.mobileOnly,
				)}
				height={mobileAsset?.height || 1024}
				imageClassName={styles.mainImg}
				src={mobileSrc}
				width={mobileAsset?.width || 1920}
			/>
		)}
	</>
)

const HeroMainImage = ({
	desktopAsset,
	mobileAsset,
	alt,
	currentIndex,
}: Props) => {
	const desktopSrc =
		desktopAsset?.type === "image" ? desktopAsset.src : undefined
	const mobileSrc =
		mobileAsset?.type === "image" ? mobileAsset.src : undefined

	if (!desktopSrc && !mobileSrc) return null

	const showDesktop = Boolean(desktopSrc)
	const showMobile = Boolean(mobileSrc)
	const swapOnSmallScreens = showDesktop && showMobile
	const isFirstSlide = currentIndex === 0
	const isSecondSlide = currentIndex === 1
	const isThirdSlide = currentIndex === 2
	const showSlide12Graphics = isFirstSlide || isSecondSlide
	const isFirstRender = useRef(true)
	const wrapperTheme = isFirstSlide
		? "slide1"
		: isSecondSlide
		? "slide2"
		: isThirdSlide
		? "slide3"
		: "default"
	const imageKey = `${currentIndex}-${desktopSrc ?? ""}-${mobileSrc ?? ""}`
	const slideImages = {
		alt,
		desktopAsset,
		desktopSrc,
		mobileAsset,
		mobileSrc,
		showDesktop,
		showMobile,
		swapOnSmallScreens,
	}

	const cameraSrc = isThirdSlide
		? "/decorative-graphics/hero/slide-3/studio-camera.svg"
		: isFirstSlide
		? "/decorative-graphics/hero/slide-1/camera.svg"
		: "/decorative-graphics/hero/slide-2/camera.svg"
	const cameraClass = isThirdSlide
		? styles.slide_3_Camera
		: isFirstSlide
		? styles.slide_1_floatCamera
		: styles.slide_2_FloatCamera
	const phoneSrc = isThirdSlide
		? "/decorative-graphics/hero/slide-3/studio-playlist.svg"
		: isFirstSlide
		? "/decorative-graphics/hero/slide-1/phone.svg"
		: "/decorative-graphics/hero/slide-2/phone.svg"
	const phoneClass = isThirdSlide
		? styles.slide_3_Playlist
		: isFirstSlide
		? styles.slide_1_floatPhone
		: styles.slide_2_FloatPhone
	const bannerReceivedSrc = isThirdSlide
		? "/decorative-graphics/hero/slide-3/studio-trim.svg"
		: isFirstSlide
		? "/decorative-graphics/hero/slide-1/banner-you-have-been.svg"
		: "/decorative-graphics/hero/slide-2/banner-you-received.svg"
	const bannerReceivedClass = isThirdSlide
		? styles.slide_3_Trim
		: isFirstSlide
		? styles.slide_1_floatBannerYouHaveBeen
		: styles.slide_2_BannerReceivedGroup

	return (
		<div className={styles.imageColumn}>
			<div className={styles.imageWrapper}>
				<AnimatePresence mode={"sync"}>
					<motion.div
						key={wrapperTheme}
						aria-hidden
						animate={{opacity: 1}}
						className={clsx(
							styles.imageWrapperBackground,
							isFirstSlide && styles.imageWrapperSlide1,
							isSecondSlide && styles.imageWrapperSlide2,
							isThirdSlide && styles.imageWrapperSlide3,
						)}
						exit={{opacity: 0}}
						initial={{opacity: isFirstRender.current ? 1 : 0}}
						onAnimationComplete={() => {
							isFirstRender.current = false
						}}
						transition={
							isFirstRender.current ? {duration: 0} : WRAPPER_FADE
						}
					/>
				</AnimatePresence>
				<div className={styles.mainImgStage}>
					<div className={styles.mainImgBox}>
						<div aria-hidden className={styles.mainImgSizer}>
							<SlideImages {...slideImages} />
						</div>
						<AnimatePresence initial={false} mode={"sync"}>
							<motion.div
								key={imageKey}
								animate={{opacity: 1}}
								className={styles.mainImgLayer}
								exit={{opacity: 0}}
								initial={{opacity: 0}}
								transition={IMAGE_FADE}
							>
								<SlideImages {...slideImages} />
							</motion.div>
						</AnimatePresence>
					</div>
					<div
						className={
							isThirdSlide
								? styles.slide_3_Graphics
								: styles.sharedFloatGraphics
						}
					>
						<LayoutFloat className={cameraClass} src={cameraSrc} />
						<motion.div
							className={bannerReceivedClass}
							layout
							layoutDependency={bannerReceivedClass}
							transition={{layout: GRAPHIC_LAYOUT}}
						>
							<img
								alt={""}
								className={styles.layoutGraphicFill}
								src={bannerReceivedSrc}
							/>
							<AnimatePresence initial={false}>
								{isSecondSlide && (
									<motion.img
										key={"slide-2-heart"}
										alt={""}
										animate={{opacity: 1, scale: 1}}
										className={styles.slide_2_FloatHeart}
										exit={{opacity: 0, scale: 0.6}}
										initial={{opacity: 0, scale: 0.6}}
										src={
											"/decorative-graphics/hero/slide-2/heart.svg"
										}
										transition={IMAGE_FADE}
									/>
								)}
							</AnimatePresence>
						</motion.div>
						<LayoutFloat className={phoneClass} src={phoneSrc} />
					</div>
					<AnimatePresence initial={false}>
						{showSlide12Graphics && (
							<motion.div
								key={"slide-1-2-party"}
								animate={{opacity: 1}}
								className={styles.graphicFadeLayer}
								exit={{opacity: 0}}
								initial={{opacity: 0}}
								transition={IMAGE_FADE}
							>
								<LayoutFloat
									className={
										isFirstSlide
											? styles.slide_1_floatBannerMeeting
											: styles.slide_2_FloatBannerParty
									}
									src={
										isFirstSlide
											? "/decorative-graphics/hero/slide-1/banner-meeting.svg"
											: "/decorative-graphics/hero/slide-2/banner-party.svg"
									}
								/>
							</motion.div>
						)}
					</AnimatePresence>
					<AnimatePresence initial={false}>
						{showSlide12Graphics && (
							<motion.div
								key={"slide-1-2-tools"}
								animate={{opacity: 1}}
								className={styles.slideToolsBelow}
								exit={{opacity: 0}}
								initial={{opacity: 0}}
								transition={IMAGE_FADE}
							>
								<motion.img
									key={
										isFirstSlide
											? "slide-1-tools"
											: "slide-2-tools"
									}
									alt={""}
									animate={{scale: 1, y: 0}}
									className={styles.slideToolsBelowImg}
									initial={{scale: 0.85, y: 10}}
									src={
										isFirstSlide
											? "/decorative-graphics/hero/slide-1/tools.svg"
											: "/decorative-graphics/hero/slide-2/tools.svg"
									}
									transition={GRAPHIC_LAYOUT}
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</div>
		</div>
	)
}

export default HeroMainImage
